import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  PhoneIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  UserIcon,
  ArrowLeftIcon,
  GlobeAltIcon,
  DocumentCheckIcon,
  MapPinIcon,
  ArrowRightIcon,
  LockClosedIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import sunnyHomesCrmLogo from 'assets/images/SH-logo.png';
import constructionBg from 'assets/images/construction_bg.png';
import { Page } from 'components/shared/Page';
import { Card } from 'components/ui';
import {
  getChecklistItems,
  createChecklistItem,
  getVehicleChecklistOptions,
  createVehicleChecklist,
  resolveVehicleToken,
  verifyUserByPhone,
} from 'services/whs/whsService';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import 'styles/app/forms/phone-input.css';
import { cleanPhoneNumber } from 'utils/phoneUtils';
import { EntityTypeEnum } from 'enum/EntityTypeEnum';
import { DocumentKeyEnum } from 'enum/DocumentKeyEnum';
import { EntityDynamicFieldsForm } from 'components/shared/dynamic-fields';
import { AttachmentsActions } from 'components/shared/AttachmentsActions';
import { MultiSelectCheckboxDropdown } from 'custom-components/shared/MultiSelectCheckboxDropdown';

export default function VehicleChecklistQrPage() {
  const { token, vehicleId: urlVehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Step state: 1 = Phone Verification, 2 = Fill Checklist Form
  const [step, setStep] = useState(1);

  // Phone Verification State
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verifyingUser, setVerifyingUser] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [verifyError, setVerifyError] = useState('');

  // Vehicle Selection State
  const [vehiclesList, setVehiclesList] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);

  // Checklist Form State
  const [checklistItemsList, setChecklistItemsList] = useState([]);
  const [itemStatuses, setItemStatuses] = useState({});
  const [hiddenItemIds, setHiddenItemIds] = useState(new Set());
  const [newItemName, setNewItemName] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [setAllValue, setSetAllValue] = useState('');
  const [defects, setDefects] = useState('');
  const [comments, setComments] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Success Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionSummary, setSubmissionSummary] = useState(null);

  const dynamicFieldsRef = useRef(null);

  // Load Reference Lists (Vehicles & Master Inspection Checklist Items)
  useEffect(() => {
    // Resolve vehicle details exclusively via ResolveVehicleToken API
    const queryToken = searchParams.get('token') || searchParams.get('vehicleId') || searchParams.get('id') || searchParams.get('t');
    const targetToken = token || urlVehicleId || queryToken;

    if (targetToken) {
      resolveVehicleToken(targetToken)
        .then((res) => {
          if (!res) return;
          const resData = res.data !== undefined ? res.data : res;
          const resolved = typeof resData === 'string' ? JSON.parse(resData) : resData;
          const target = resolved?.vehicle || resolved?.Vehicle || resolved;
          const vId = target?.VehicleId || target?.vehicleId || target?.VehicleID || target?.id || target?.Id;
          if (vId) {
            setSelectedVehicleId(String(vId).toLowerCase());
            setSelectedVehicleDetails(target);
          }
        })
        .catch((err) => {
          console.error("Failed to resolve vehicle token:", err);
        });
    }
  }, [token, urlVehicleId, searchParams]);

  // Load Checklist Items ONLY after user phone verification succeeds (Step 2)
  useEffect(() => {
    if (step === 2 && checklistItemsList.length === 0) {
      const activeCompanyId = selectedVehicleDetails?.CompanyId || selectedVehicleDetails?.companyId || verifiedUser?.externalCompanyId || '';
      getChecklistItems(activeCompanyId)
        .then((res) => {
          if (res?.statusCode === 200 && res.data) {
            const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
            const items = parsed || [];
            setChecklistItemsList(items);

            const defaults = {};
            items.forEach((item) => {
              if (item.Id) defaults[item.Id.toLowerCase()] = 'Pass';
            });
            setItemStatuses(defaults);
          }
        })
        .catch(console.error);
    }
  }, [step, checklistItemsList.length, selectedVehicleDetails, verifiedUser]);

  // Handle Phone Verification Submit
  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    let cleanPhone = cleanPhoneNumber(phoneNumber) || phoneNumber?.trim();
    if (!cleanPhone) {
      setVerifyError('Please enter a valid phone number.');
      return;
    }
    if (!cleanPhone.startsWith('+')) {
      const digitsOnly = cleanPhone.replace(/\D/g, '');
      cleanPhone = digitsOnly.startsWith('61') ? `+${digitsOnly}` : `+61${digitsOnly.replace(/^0+/, '')}`;
    }

    setVerifyingUser(true);
    setVerifyError('');

    try {
      const companyId = selectedVehicleDetails?.CompanyId || selectedVehicleDetails?.companyId || '';
      const res = await verifyUserByPhone(cleanPhone, companyId);
      let apiData = res?.data;
      if (typeof apiData === 'string') {
        try {
          apiData = JSON.parse(apiData);
        } catch (e) {
          console.error('Failed to parse VerifyUserByPhone json:', e);
        }
      }

      const rawUser = apiData?.user || res?.user || apiData || res;
      const userId = rawUser?.UserId || rawUser?.userId || rawUser?.id || rawUser?.Id || rawUser?.Userid;

      if ((res?.statusCode === 200 || res?.success || rawUser) && rawUser && (userId || rawUser.FullName || rawUser.fullName)) {
        const userObj = {
          id: userId || 'verified-user',
          name:
            rawUser.FullName ||
            rawUser.fullName ||
            rawUser.name ||
            '-',
          phone: rawUser.PhoneNumber || rawUser.phoneNumber || cleanPhone,
          email: rawUser.Email || rawUser.email || '',
          externalCompanyId: rawUser.ExternalCompanyId || rawUser.externalCompanyId || companyId,
          raw: rawUser,
        };
        setVerifiedUser(userObj);
        toast.success(`Welcome, ${userObj.name}! Phone verified successfully.`);
        setStep(2);
      } else {
        const errorMsg = res?.message || res?.error || 'No user found with this phone number. Please contact administration.';
        setVerifyError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Phone verification error:', err);
      const catchMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'An error occurred during verification. Please try again.';
      setVerifyError(catchMsg);
      toast.error(catchMsg);
    } finally {
      setVerifyingUser(false);
    }
  };

  // Handle Vehicle Dropdown Change
  const handleVehicleChange = (val) => {
    setSelectedVehicleId(val || '');
    const found = vehiclesList.find((v) => v.VehicleId?.toLowerCase() === val?.toLowerCase());
    setSelectedVehicleDetails(found || null);
  };

  // Status Change for Individual Checklist Item
  const handleStatusChange = (itemId, status) => {
    setSetAllValue('');
    setItemStatuses((prev) => ({
      ...prev,
      [itemId.toLowerCase()]: status,
    }));
  };

  // Remove item for this checklist
  const handleRemoveItem = (itemId) => {
    setHiddenItemIds((prev) => {
      const next = new Set(prev);
      next.add(itemId.toLowerCase());
      return next;
    });
  };

  // Inline Add New Item
  const handleAddNewItem = async () => {
    const name = newItemName.trim();
    if (!name || addingItem) return;

    setAddingItem(true);
    try {
      const activeCompanyId = selectedVehicleDetails?.CompanyId || selectedVehicleDetails?.companyId || verifiedUser?.externalCompanyId || '';
      const activeUserId = verifiedUser?.id || verifiedUser?.raw?.UserId || verifiedUser?.raw?.userId || verifiedUser?.raw?.id || '';
      const res = await createChecklistItem(name, activeCompanyId, activeUserId);
      if (res?.statusCode === 200 && res.data) {
        const newId = res.data;
        setChecklistItemsList((prev) => [...prev, { Id: newId, ItemName: name }]);
        setItemStatuses((prev) => ({ ...prev, [newId.toLowerCase()]: 'Pass' }));
        setNewItemName('');
        toast.success('Checklist item added.');
      } else {
        toast.error(res?.message || 'Failed to add checklist item.');
      }
    } catch (err) {
      console.error('Failed to add item:', err);
      toast.error('Failed to add checklist item.');
    } finally {
      setAddingItem(false);
    }
  };

  // Reset to Phone Verification Step
  const handleResetForm = () => {
    setStep(1);
    setPhoneNumber('');
    setVerifiedUser(null);
    setVerifyError('');
    setDefects('');
    setComments('');
    setUploadedFiles([]);
    setSetAllValue('');

    const defaults = {};
    checklistItemsList.forEach((item) => {
      if (item.Id) defaults[item.Id.toLowerCase()] = 'Pass';
    });
    setItemStatuses(defaults);
  };

  // Close Confirmation Modal & Return to Verify Page
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSubmissionSummary(null);
    handleResetForm();
  };

  // Handle Form Submission
  const handleSubmitChecklist = async (e) => {
    e.preventDefault();

    if (!selectedVehicleId) {
      toast.error('Please select a vehicle before submitting.');
      return;
    }

    const driverId = verifiedUser?.id || verifiedUser?.raw?.UserId || verifiedUser?.raw?.userId || verifiedUser?.raw?.id;
    if (!verifiedUser || !driverId) {
      toast.error('User verification required.');
      setStep(1);
      return;
    }

    setSubmitting(true);

    try {
      const itemStatusesPayload = Object.entries(itemStatuses)
        .filter(([itemId]) => !hiddenItemIds.has(itemId))
        .map(([itemId, statusStr]) => {
          let numericStatus = 1; // Pass
          if (statusStr === 'Fail') numericStatus = 2;
          if (statusStr === 'N/A') numericStatus = 0;
          return {
            ChecklistItemId: itemId,
            ItemStatus: numericStatus,
          };
        });

      const activeCompanyId = selectedVehicleDetails?.CompanyId || selectedVehicleDetails?.companyId || verifiedUser?.externalCompanyId || '';
      const payload = {
        VehicleId: selectedVehicleId,
        CompanyId: activeCompanyId,
        DriverIds: [String(driverId).toLowerCase()],
        DefectsDescription: defects.trim(),
        DriverComments: comments.trim(),
        ItemStatuses: itemStatusesPayload,
        Attachments: uploadedFiles.filter((f) => f instanceof File),
      };

      const res = await createVehicleChecklist(payload, driverId);

      if (res?.statusCode === 200 || res?.status === 'Success' || res?.data) {
        const createdId =
          typeof res.data === 'string'
            ? res.data
            : res?.data?.ChecklistId || res?.data?.id || res?.ChecklistId || res?.id || res?.data;

        if (createdId && dynamicFieldsRef.current) {
          try {
            await dynamicFieldsRef.current.save(createdId);
          } catch (dfErr) {
            console.error('Failed to save dynamic fields:', dfErr);
          }
        }

        const passCount = Object.entries(itemStatuses)
          .filter(([itemId]) => !hiddenItemIds.has(itemId))
          .filter(([_, status]) => status === 'Pass').length;
        const failCount = Object.entries(itemStatuses)
          .filter(([itemId]) => !hiddenItemIds.has(itemId))
          .filter(([_, status]) => status === 'Fail').length;
        const naCount = Object.entries(itemStatuses)
          .filter(([itemId]) => !hiddenItemIds.has(itemId))
          .filter(([_, status]) => status === 'N/A').length;

        setSubmissionSummary({
          vehicleName: selectedVehicleDetails ? `${selectedVehicleDetails.Make} ${selectedVehicleDetails.Model}` : 'Vehicle',
          rego: selectedVehicleDetails?.RegistrationNumber || selectedVehicleDetails?.Registration || '—',
          userName: verifiedUser?.name || 'User',
          userPhone: verifiedUser?.phone || '',
          userEmail: verifiedUser?.email || '',
          totalItems: itemStatusesPayload.length,
          passCount,
          failCount,
          naCount,
          submittedAt: new Date().toLocaleString('en-AU', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
        });

        setShowSuccessModal(true);
        toast.success('Vehicle pre-start checklist submitted successfully!');
      } else {
        toast.error(res?.message || 'Failed to submit vehicle checklist.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('An error occurred while submitting checklist.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page title="Vehicle Pre-Start Checklist">
      {/* Full Screen Loading Overlay on Submission */}
      {submitting && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-200">
          <div className="bg-white/95 backdrop-blur-2xl rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl border border-white/80 flex flex-col items-center">
            <div className="size-16 rounded-2xl bg-primary-50 border border-primary-200 flex items-center justify-center mb-4 shadow-sm">
              <ArrowPathIcon className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1.5">
              Submitting Inspection Checklist...
            </h3>
            <p className="text-xs font-semibold text-slate-500 max-w-xs leading-relaxed">
              {uploadedFiles.length > 0
                ? "Uploading inspection photos and saving checklist details. Please do not close or navigate away..."
                : "Saving inspection checklist details. Please do not close or navigate away..."}
            </p>
          </div>
        </div>
      )}

      <div className="relative flex w-full min-h-screen items-center justify-center bg-slate-50 px-2.5 pt-5 pb-6 sm:p-4 md:p-6 overflow-y-auto overflow-x-hidden selection:bg-primary-500/30 selection:text-slate-900 font-sans">
        {/* Background construction image + backdrop overlays */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700 opacity-60"
          style={{ backgroundImage: `url(${constructionBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50/90 via-slate-100/80 to-primary-100/30 backdrop-blur-[5px]" />

        {/* Ambient blurred glow circles matching WhsQrPage */}
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-primary-300/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-80 h-80 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" />

        <div className="relative z-10 w-full max-w-full sm:max-w-lg my-0 sm:my-3 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-x-hidden">
          {/* Header Branding Container */}
          <div className="mb-3 text-center">
            <div className="mb-2 flex items-center justify-center space-x-2 rounded-2xl border border-white/80 bg-white/95 px-3.5 py-1.5 mx-auto w-fit backdrop-blur-md shadow-xs">
              <img src={sunnyHomesCrmLogo} className="h-7 object-contain" alt="Sunny Homes logo" />
            </div>

            <div className="flex flex-col items-center gap-0.5">
              <h1 className="text-base font-bold text-slate-900 tracking-tight">Vehicle Pre-Start Checklist</h1>
              <p className="text-[11px] font-semibold text-slate-500">
                WHS &amp; Fleet Safety System
              </p>
            </div>
          </div>

          {/* Main Card Container styled exactly like WhsQrPage Card */}
          <Card className="bg-white/90 px-3.5 py-4 sm:p-5 md:p-6 backdrop-blur-2xl rounded-2xl sm:rounded-3xl border border-white/60 relative overflow-hidden shadow-none">
            {step === 1 ? (
              /* ── STEP 1: Phone Verification ──────────────────────── */
              <div className="space-y-4">
                {selectedVehicleDetails && (
                  <div className="bg-slate-50/90 border border-slate-200/80 p-3.5 rounded-2xl text-left space-y-1 shadow-xs">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        VEHICLE
                      </p>
                      <span className="text-[10px] font-bold text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                        ID: {selectedVehicleDetails.VehicleDisplayId || selectedVehicleDetails.VehicleNumber || selectedVehicleDetails.VehicleId?.substring(0, 5) || '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900">
                        {selectedVehicleDetails.Make} {selectedVehicleDetails.Model}
                      </p>
                      {selectedVehicleDetails.VehicleType && (
                        <span className="text-[11px] font-bold text-blue-600 bg-blue-50 border border-blue-200/80 px-2 py-0.5 rounded-md">
                          {selectedVehicleDetails.VehicleType}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-semibold flex items-center gap-1.5 pt-0.5">
                      <MapPinIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>Registration: {selectedVehicleDetails.RegistrationNumber || selectedVehicleDetails.Registration || '—'}</span>
                    </p>
                  </div>
                )}

                <form onSubmit={handleVerifyPhone} className="space-y-4">
                  {verifyError && (
                    <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-700 flex items-start gap-2">
                      <ExclamationTriangleIcon className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" />
                      <span>{verifyError}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Mobile Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <div
                      className="relative flex items-center rounded-2xl border border-blue-500 focus-within:ring-4 focus-within:ring-blue-100 bg-white p-2.5 shadow-xs transition-all"
                      onPaste={(e) => {
                        const pasted = e.clipboardData?.getData('text');
                        if (pasted) {
                          let cleaned = pasted.trim().replace(/\s+/g, '');
                          if (cleaned.startsWith('04') || cleaned.startsWith('02') || cleaned.startsWith('03') || cleaned.startsWith('07') || cleaned.startsWith('08')) {
                            cleaned = `+61${cleaned.substring(1, 10)}`;
                          } else if (!cleaned.startsWith('+') && cleaned.length >= 9) {
                            cleaned = `+61${cleaned.substring(0, 9)}`;
                          }
                          if (cleaned) {
                            e.preventDefault();
                            setPhoneNumber(cleaned);
                          }
                        }
                      }}
                    >
                      <style>{`
                        .PhoneInput {
                          display: flex;
                          align-items: center;
                          width: 100%;
                          gap: 8px;
                        }
                        .PhoneInputCountry {
                          display: flex;
                          align-items: center;
                          gap: 4px;
                        }
                        .PhoneInputInput {
                          width: 100%;
                          background: transparent;
                          font-size: 13px;
                          font-weight: 600;
                          color: #1e293b;
                          outline: none;
                          border: none;
                          font-family: monospace;
                        }
                      `}</style>
                      <PhoneInput
                        international
                        countryCallingCodeEditable={false}
                        defaultCountry="AU"
                        value={phoneNumber}
                        onChange={(val) => {
                          if (!val) {
                            setPhoneNumber('');
                            return;
                          }
                          const digits = val.replace(/\D/g, '');
                          if (digits.length <= 12) {
                            setPhoneNumber(val);
                          }
                        }}
                        numberInputProps={{ maxLength: 15 }}
                        placeholder="Enter mobile phone number"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={verifyingUser || !phoneNumber}
                      className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 active:scale-[0.98] text-white font-bold text-xs shadow-md transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {verifyingUser ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          <span>Verifying...</span>
                        </>
                      ) : (
                        <>
                          <span>Next</span>
                          <ArrowRightIcon className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              /* ── STEP 2: Vehicle Checklist Form ──────────────────── */
              <div className="space-y-4">
                {/* Top Action Header with Add Fields button */}
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
                  <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Pre-Start Inspection
                  </h2>
                  <button
                    type="button"
                    onClick={() => dynamicFieldsRef.current?.openAddFieldModal()}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-xl transition-all cursor-pointer border border-primary-200 shadow-xs"
                  >
                    <PlusCircleIcon className="w-4 h-4" />
                    <span>Add Fields</span>
                  </button>
                </div>

                {/* Verified User Header Card */}
                <div className="bg-slate-50/90 border border-slate-200/80 p-3.5 rounded-2xl text-left shadow-xs flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-9 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      <UserIcon className="w-5 h-5" />
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        VERIFIED USER DETAILS
                      </p>
                      <p className="text-xs font-bold text-slate-900">
                        {verifiedUser?.name || 'User'}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] font-semibold text-slate-500">
                        <span>📞 {verifiedUser?.phone}</span>
                        {verifiedUser?.email && <span>✉️ {verifiedUser?.email}</span>}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleResetForm}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-900 bg-white border border-slate-200 px-2.5 py-1 rounded-xl transition-colors cursor-pointer shrink-0"
                  >
                    <ArrowLeftIcon className="w-3 h-3" />
                    <span>Change</span>
                  </button>
                </div>

                <form onSubmit={handleSubmitChecklist} className="space-y-4">

                  {/* Section 2: Inspection Checklist Items */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Inspection Items
                      </h3>

                      <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-xl">
                        <span className="text-[10px] font-bold text-slate-500">Set All:</span>
                        <select
                          value={setAllValue}
                          onChange={(e) => {
                            const status = e.target.value;
                            setSetAllValue(status);
                            if (!status) return;
                            const updated = {};
                            checklistItemsList.forEach((item) => {
                              if (item.Id) updated[item.Id.toLowerCase()] = status;
                            });
                            setItemStatuses(updated);
                          }}
                          className="text-xs font-bold rounded border border-slate-200 bg-white px-1.5 py-0.5 text-slate-800 focus:outline-none"
                        >
                          <option value="">Status...</option>
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </div>
                    </div>

                    <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl overflow-hidden divide-y divide-slate-200/60 shadow-xs">
                      {checklistItemsList
                        .filter((item) => !hiddenItemIds.has(item.Id?.toLowerCase()))
                        .map((item) => {
                          const currentVal = itemStatuses[item.Id?.toLowerCase()] || 'Pass';
                          return (
                            <div key={item.Id} className="flex items-center justify-between p-2.5 gap-2">
                              <span className="text-xs font-bold text-slate-800 leading-tight">
                                {item.ItemName}
                              </span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleStatusChange(item.Id, 'Pass')}
                                    className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                                      currentVal === 'Pass'
                                        ? 'bg-emerald-500 text-white shadow-xs'
                                        : 'text-slate-500 hover:text-slate-900'
                                    }`}
                                  >
                                    Pass
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleStatusChange(item.Id, 'Fail')}
                                    className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                                      currentVal === 'Fail'
                                        ? 'bg-rose-600 text-white shadow-xs'
                                        : 'text-slate-500 hover:text-slate-900'
                                    }`}
                                  >
                                    Fail
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleStatusChange(item.Id, 'N/A')}
                                    className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                                      currentVal === 'N/A'
                                        ? 'bg-slate-600 text-white shadow-xs'
                                        : 'text-slate-500 hover:text-slate-900'
                                    }`}
                                  >
                                    N/A
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItem(item.Id)}
                                  title="Remove from this checklist"
                                  className="flex items-center justify-center size-7 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                      {/* Add Custom Item Inline */}
                      <div className="flex items-center gap-2 p-2 bg-white">
                        <input
                          type="text"
                          value={newItemName}
                          onChange={(e) => setNewItemName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddNewItem();
                            }
                          }}
                          placeholder="Add item for this inspection..."
                          disabled={addingItem}
                          className="block w-full rounded-xl border border-slate-200 bg-slate-50/80 py-1.5 px-3 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={handleAddNewItem}
                          disabled={addingItem || !newItemName.trim()}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-primary-600 border border-primary-200 rounded-xl hover:bg-primary-50 disabled:opacity-50 shrink-0 cursor-pointer"
                        >
                          <PlusCircleIcon className="w-4 h-4" />
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Defects & Comments */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Defects Description
                      </label>
                      <textarea
                        rows={2}
                        value={defects}
                        onChange={(e) => setDefects(e.target.value)}
                        placeholder="Describe any defects found during inspection (if any)..."
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50/80 p-3 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Driver Comments
                      </label>
                      <textarea
                        rows={2}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Provide any general comments..."
                        className="block w-full rounded-2xl border border-slate-200 bg-slate-50/80 p-3 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      />
                    </div>

                    {/* Section 4: Attachments */}
                    <AttachmentsActions
                      showLabel={true}
                      files={uploadedFiles}
                      onChange={setUploadedFiles}
                      addLabel="Upload Inspection Photos"
                      downloadKey={DocumentKeyEnum.VehicleChecklist}
                    />

                    {/* Section 5: Dynamic Custom Fields */}
                    <EntityDynamicFieldsForm
                      ref={dynamicFieldsRef}
                      entityTypeCode={EntityTypeEnum.VehicleChecklist}
                      companyId={selectedVehicleDetails?.CompanyId || selectedVehicleDetails?.companyId || verifiedUser?.externalCompanyId}
                      userId={verifiedUser?.id || verifiedUser?.raw?.UserId || verifiedUser?.raw?.userId || verifiedUser?.raw?.id}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-2 border-t border-slate-200/60 flex gap-2.5">
                    <button
                      type="button"
                      onClick={handleResetForm}
                      className="flex-1 py-3 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-2 py-3 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
                    >
                      {submitting ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin text-white" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-4 h-4 text-white" />
                          <span>Submit Checklist</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </Card>

          {/* Footer link matching WhsQrPage */}
          <div className="mt-4 text-center">
            <a
              href={window.location.origin}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary-600 font-bold transition-all cursor-pointer"
            >
              <GlobeAltIcon className="h-4 w-4 text-slate-400 hover:text-primary-600" />
              <span>Visit Sunny Homes Site</span>
            </a>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showSuccessModal && submissionSummary && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 animate-in zoom-in-95 duration-200 border border-slate-100 text-center">
            {/* Icon & Title Header */}
            <div className="flex flex-col items-center gap-2">
              <div className="size-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner">
                <CheckCircleIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Checklist Submitted!</h3>
              <p className="text-xs text-slate-500 font-medium">
                Vehicle pre-start inspection recorded successfully.
              </p>
            </div>

            {/* Summary Container */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 text-left space-y-3 text-xs">
              <div className="pb-2.5 border-b border-slate-200/60 flex justify-between items-start">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Scanned Vehicle</p>
                  <p className="font-bold text-slate-900 text-sm">{submissionSummary.vehicleName}</p>
                  <p className="text-slate-500 font-semibold">Rego: {submissionSummary.rego}</p>
                </div>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                  Confirmed
                </span>
              </div>

              <div className="pb-2.5 border-b border-slate-200/60">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Submitted By</p>
                <p className="font-bold text-slate-900">{submissionSummary.userName}</p>
                <p className="text-slate-500 font-semibold">📞 {submissionSummary.userPhone}</p>
                {submissionSummary.userEmail && (
                  <p className="text-slate-500 font-semibold">✉️ {submissionSummary.userEmail}</p>
                )}
              </div>

              <div className="pb-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  Inspection Summary ({submissionSummary.totalItems} Items)
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-emerald-50 border border-emerald-200/80 p-2 rounded-xl">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase">Pass</p>
                    <p className="text-base font-bold text-emerald-700">{submissionSummary.passCount}</p>
                  </div>
                  <div className="bg-rose-50 border border-rose-200/80 p-2 rounded-xl">
                    <p className="text-[10px] font-bold text-rose-600 uppercase">Fail</p>
                    <p className="text-base font-bold text-rose-700">{submissionSummary.failCount}</p>
                  </div>
                  <div className="bg-slate-100 border border-slate-200/80 p-2 rounded-xl">
                    <p className="text-[10px] font-bold text-slate-500 uppercase">N/A</p>
                    <p className="text-base font-bold text-slate-700">{submissionSummary.naCount}</p>
                  </div>
                </div>
              </div>

              <div className="pt-1 text-[10px] text-slate-400 font-medium text-right">
                Submitted: {submissionSummary.submittedAt}
              </div>
            </div>

            {/* Done Button */}
            <button
              type="button"
              onClick={handleCloseSuccessModal}
              className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 active:scale-[0.98] text-white font-bold rounded-2xl shadow-lg transition-all text-xs flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Done &amp; Return to Verify</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </Page>
  );
}
